import { PiCaretDownLight } from 'react-icons/pi';
import MenuDropdown from '../menu-dropdown';
import type { DropdownItem } from '../dropdown';
import Button from '../../../atoms/buttons/button';
import type {
  ComponentSize,
  ComponentVariant,
  ComponentAppearance,
} from 'src/modules/shared/types/component.type';
import { useCallback, useMemo } from 'react';
import Loading from '../../../atoms/loading';
import { Text } from 'src/modules/shared/components/atoms/text';
import { getControlTextSize } from 'src/modules/shared/components/atoms/text/text.styles';
import type { DropdownPlacement } from 'src/modules/shared/components/molecules/dropdowns/dropdown';

export type TagDropdownOption = Omit<DropdownItem, 'onClick'> & {
  id: string | number;
};

interface TagDropdownProps {
  content: string | React.ReactNode;
  variant?: ComponentVariant;
  size?: ComponentSize;
  appearance?: ComponentAppearance;
  isLoading?: boolean;
  disabled?: boolean;
  options?: TagDropdownOption[];
  onOptionSelect?: (option: TagDropdownOption) => void;
  className?: string;
  placement?: DropdownPlacement;
  onOpenChange?: (isOpen: boolean) => void;
  mobileTitle?: React.ReactNode;
}

export const TagDropdown: React.FC<TagDropdownProps> = ({
  content,
  variant = 'primary',
  size = 'md',
  appearance = 'filled',
  isLoading,
  disabled = false,
  options = [],
  onOptionSelect,
  className = '',
  placement = 'bottom-start',
  onOpenChange,
  mobileTitle,
}) => {
  const handleSelect = useCallback(
    (option: TagDropdownOption) => {
      onOptionSelect?.(option);
    },
    [onOptionSelect]
  );

  const dropdownOptions = useMemo(() => {
    return options.map((option) => ({
      ...option,
      onClick: () => handleSelect(option),
    }));
  }, [options, handleSelect]);

  const renderHandler = () => (
    <Button
      type="button"
      variant={variant}
      size={size}
      appearance={appearance}
      className={`rounded-full !gap-1 !pr-2 ${className}`}
      disabled={disabled}
    >
      <span className="min-w-0 flex items-center gap-1">
        {typeof content === 'string' ? (
          <span className="truncate">
            <Text size={getControlTextSize(size)} color="inherit">
              {content}
            </Text>
          </span>
        ) : (
          content
        )}
      </span>
      {isLoading ? (
        <Loading size="2xs" variant={variant} />
      ) : (
        <PiCaretDownLight className="h-4 w-4 flex-shrink-0" />
      )}
    </Button>
  );

  return (
    <MenuDropdown
      isLoading={isLoading}
      disabled={disabled}
      handlerContainerClassName="w-fit"
      placement={placement}
      handlerComponent={renderHandler()}
      size={size}
      items={dropdownOptions}
      onOpenChange={onOpenChange}
      mobileTitle={mobileTitle}
    />
  );
};
